/*
 * Copyright (C) 2023 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { inject, Injectable } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Resource } from '@ngxp/rest';
import { isNil } from 'lodash-es';
import { identity, Observable, OperatorFunction } from 'rxjs';
import { map } from 'rxjs/operators';
import { hasStateResourceError, StateResource } from '../resource/resource.util';
import { HttpError, InvalidParam, ProblemDetail } from '../tech.model';
import { isNotUndefined } from '../tech.util';
import { setInvalidParamValidationError } from '../validation/tech.validation.util';

@Injectable()
export abstract class AbstractFormService<T extends Resource> {
  protected readonly formBuilder = inject(UntypedFormBuilder);

  public form: UntypedFormGroup = this.initForm();
  pathPrefix: string;
  source: any;

  private readonly PROBLEM_DETAIL_INVALID_PARAMS_KEY: string = 'invalidParams';

  protected abstract initForm(): UntypedFormGroup;

  public submit(afterSubmit: OperatorFunction<StateResource<T>, StateResource<T>> = identity): Observable<StateResource<T>> {
    this.form.setErrors(null);
    return this.doSubmit().pipe(
      afterSubmit,
      map((result) => this.handleResponse(result)),
    );
  }

  abstract doSubmit(): Observable<StateResource<T>>;

  handleResponse(result: StateResource<T>): StateResource<T> {
    if (result.loading) return result;
    if (hasStateResourceError(result)) {
      this.handleError(result.error);
    }
    return result;
  }

  handleError(error: HttpError): void {
    if (this.hasError(error)) {
      this.setErrorByProblemDetail(<ProblemDetail>error);
    }
  }

  private hasError(error: HttpError): boolean {
    return isNotUndefined((<ProblemDetail>error)[this.PROBLEM_DETAIL_INVALID_PARAMS_KEY]);
  }

  setErrorByProblemDetail(error: ProblemDetail): void {
    error[this.PROBLEM_DETAIL_INVALID_PARAMS_KEY].forEach((invalidParam: InvalidParam) => {
      if (invalidParam.name === this.getPathPrefix()) {
        this.form.setErrors({ [invalidParam.reason]: invalidParam });
      } else {
        setInvalidParamValidationError(this.form, invalidParam, this.getPathPrefix());
      }
    });
  }

  protected abstract getPathPrefix(): string;

  public patch(valueToPatch: any): void {
    this.form.reset();
    this.form.patchValue(valueToPatch);

    this.source = valueToPatch;
  }

  getFormValue(): any {
    return this.form.value;
  }

  protected getSourceValue(): any {
    return this.source;
  }

  public isPatch(): boolean {
    return !isNil(this.source);
  }

  public reset(): void {
    this.form.reset();
  }

  public isInvalid(): boolean {
    return this.form.invalid;
  }
}
